import { Injectable } from '@angular/core';
import { ByteBuffer } from 'flatbuffers';
import type { Feature, FeatureCollection, MultiPolygon } from 'geojson';
import {
    collectRings,
    readFlatBufferInt32,
    readFlatBufferString,
    ringsToMultiPolygon,
} from './boundary-geometry.util';

/**
 * Decodes binary FlatBuffers boundary blobs (.bin) into GeoJSON.
 *
 * Binary layout (reverse-engineered):
 *
 *   Root table:
 *     field 0 (vtable 4) — string: type label (e.g. "cities" / "districts")
 *     field 1 (vtable 6) — string: parent GUID (region / city id)
 *     field 2 (vtable 8) — vector<table>: child unit tables (cities or districts)
 *
 *   City table:
 *     field 0 (vtable 4) — (unused by decoder)
 *     field 1 (vtable 6) — string: city GUID
 *     field 2 (vtable 8) — string: city name
 *     field 3 (vtable 10) — table: boundary tree root
 *
 *   Boundary tree node (recursive):
 *     field 0 (vtable 4) — offset → vector
 *       If entries point to coord tables → leaf (collect coordinates)
 *       If entries point to more tree nodes → recurse
 *
 *   Coordinate table (12 bytes: soffset + 2 × float32):
 *     field 0 (vtable 4, +4 bytes) — float32: longitude
 *     field 1 (vtable 6, +8 bytes) — float32: latitude
 */
@Injectable({ providedIn: 'root' })
export class BoundaryDecoderService {

    decodeCityBoundaries(_regionKey: string, buffer: ArrayBuffer): FeatureCollection<MultiPolygon> {
        const bytes = new Uint8Array(buffer);
        const bb = new ByteBuffer(bytes);
        const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        const root = bb.readInt32(0);

        const citiesFieldOff = bb.__offset(root, 8);
        if (!citiesFieldOff) return this.empty();

        const citiesLen = bb.__vector_len(root + citiesFieldOff);
        const citiesStart = bb.__vector(root + citiesFieldOff);

        const features: Feature<MultiPolygon>[] = [];

        for (let i = 0; i < citiesLen; i++) {
            const cityPos = bb.__indirect(citiesStart + i * 4);
            const cityGuid = readFlatBufferString(bb, cityPos, 6);
            const cityName = readFlatBufferString(bb, cityPos, 8);

            const boundaryTreeOff = bb.__offset(cityPos, 10);
            if (!boundaryTreeOff) continue;

            const treeRootPos = cityPos + boundaryTreeOff + dv.getInt32(cityPos + boundaryTreeOff, true);
            const rings: [number, number][][] = [];
            collectRings(dv, bytes, treeRootPos, 0, rings);

            const coordinates = ringsToMultiPolygon(rings);
            if (coordinates.length === 0) continue;

            features.push({
                type: 'Feature',
                properties: { city_id: cityGuid, city_name: cityName },
                geometry: { type: 'MultiPolygon', coordinates },
            });
        }

        return { type: 'FeatureCollection', features };
    }

    /** District blobs use the same unit table layout as cities (see root field 8). */
    decodeDistrictBoundaries(_cityKey: string, buffer: ArrayBuffer): FeatureCollection<MultiPolygon> {
        const bytes = new Uint8Array(buffer);
        const bb = new ByteBuffer(bytes);
        const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        const root = bb.readInt32(0);

        const districtsFieldOff = bb.__offset(root, 8);
        if (!districtsFieldOff) return this.empty();

        const districtsLen = bb.__vector_len(root + districtsFieldOff);
        const districtsStart = bb.__vector(root + districtsFieldOff);

        const features: Feature<MultiPolygon>[] = [];

        for (let i = 0; i < districtsLen; i++) {
            const districtPos = bb.__indirect(districtsStart + i * 4);
            const districtAccessKey = this.resolveDistrictAccessKey(bb, districtPos, i);
            const districtName = readFlatBufferString(bb, districtPos, 8);

            const boundaryTreeOff = bb.__offset(districtPos, 10);
            if (!boundaryTreeOff) continue;

            const treeRootPos = districtPos + boundaryTreeOff + dv.getInt32(districtPos + boundaryTreeOff, true);
            const rings: [number, number][][] = [];
            collectRings(dv, bytes, treeRootPos, 0, rings);

            const coordinates = ringsToMultiPolygon(rings);
            if (coordinates.length === 0) continue;

            features.push({
                type: 'Feature',
                properties: {
                    district_access_key: districtAccessKey,
                    district_name: districtName,
                },
                geometry: { type: 'MultiPolygon', coordinates },
            });
        }

        return { type: 'FeatureCollection', features };
    }

    mergeFeatureCollections(collections: FeatureCollection<MultiPolygon>[]): FeatureCollection<MultiPolygon> {
        const features: Feature<MultiPolygon>[] = [];
        for (const fc of collections) {
            features.push(...fc.features);
        }
        return { type: 'FeatureCollection', features };
    }

    private empty(): FeatureCollection<MultiPolygon> {
        return { type: 'FeatureCollection', features: [] };
    }

    /** District id may be a GUID string or numeric id depending on blob version. */
    private resolveDistrictAccessKey(bb: ByteBuffer, districtPos: number, index: number): string {
        try {
            const asString = readFlatBufferString(bb, districtPos, 6);
            if (asString?.trim()) return asString.trim();
        } catch {
            /* wrong slot type */
        }
        try {
            const at6 = readFlatBufferInt32(bb, districtPos, 6);
            if (at6 != null && Number.isFinite(at6)) return String(at6);
        } catch {
            /* wrong slot type */
        }
        try {
            const at4 = readFlatBufferInt32(bb, districtPos, 4);
            if (at4 != null && Number.isFinite(at4)) return String(at4);
        } catch {
            /* wrong slot type */
        }
        return `d-${index}`;
    }
}
